import { apiMutate } from "~/utils/apiClient";

const { VITE_SEARCH_API } = import.meta.env;

export type SearchEntityType = "project" | "dataset" | "conversation";
export const SEARCH_ENTITY_TYPES: SearchEntityType[] = ["project", "dataset", "conversation"];

export interface SearchHit {
  entity_type: SearchEntityType;
  entity_id: string;
  title: string;
  /** Indexed text with matched terms in `<em>`. Null when only the vector leg matched. */
  snippet: string | null;
  score: number;
  /** 1-based rank within a leg, or null when that leg did not return the entity. */
  vector_rank: number | null;
  lexical_rank: number | null;
}

export interface SearchResponse {
  hits: SearchHit[];
  /** A floor: each leg stops at its limit. */
  type_counts: Partial<Record<SearchEntityType, number>>;
  /** Requested types the caller may see nothing of. */
  inaccessible_types: SearchEntityType[];
  vector_candidates: number;
  lexical_candidates: number;
  took_ms: number;
}

export interface SearchIndexTypeStatus {
  entity_type: SearchEntityType;
  entities: number;
  chunks: number;
  indexed_at: string | null;
  /** Rows on the model version search reads. */
  current_chunks: number;
  /** Entities with no text to index, as of the last full sync. */
  blank: number | null;
}

/** How many chunks fall in one length bin. `upper` is null for the overflow bin. */
export interface SearchChunkSizeBucket {
  lower: number;
  upper: number | null;
  chunks: number;
  over_max: boolean;
}

export interface SearchIndexStatus {
  types: SearchIndexTypeStatus[];
  chunk_sizes: SearchChunkSizeBucket[];
}

export interface SearchIndexTypeResult {
  entity_type: SearchEntityType;
  read: number;
  embedded: number;
  chunks: number;
  unchanged: number;
  blank: number;
  pruned: number;
}

/** A recorded indexing run, filled in per type as it goes. */
export interface SearchIndexRun {
  id: string;
  started_at: string;
  completed_at: string | null;
  error: string | null;
  results: SearchIndexTypeResult[];
}

export interface SearchIndexRunsResponse {
  runs: SearchIndexRun[];
}

export interface SearchOrphanTypeStatus {
  entity_type: SearchEntityType;
  in_source: number;
  orphans: number;
  sample: string[];
  /** Why these orphans may not be deleted. */
  blocked: string | null;
}

export interface SearchOrphanResponse {
  types: SearchOrphanTypeStatus[];
}

/** Omitted when every type is selected, matching the backend default and its cache key. */
const appendEntityTypes = (params: URLSearchParams, entityTypes: SearchEntityType[]) => {
  if (entityTypes.length < SEARCH_ENTITY_TYPES.length) {
    entityTypes.forEach((entityType) => params.append("entity_types", entityType));
  }
};

/** Null when there is nothing to search for, which is SWR's signal not to fetch. */
export const searchPath = (
  query: string,
  entityTypes: SearchEntityType[],
  limit: number,
  updatedWithinDays: number | null
): string | null => {
  if (!query.trim()) return null;
  const params = new URLSearchParams({ q: query, limit: String(limit) });
  appendEntityTypes(params, entityTypes);
  if (updatedWithinDays !== null) {
    params.set("updated_within_days", String(updatedWithinDays));
  }
  return `${VITE_SEARCH_API}?${params}`;
};

export const searchIndexPath = () => `${VITE_SEARCH_API}/index`;

/** Whole-corpus, since an indexing run cannot show that an entity is gone. */
export const searchIndexOrphansPath = () => `${VITE_SEARCH_API}/index/orphans`;

/** Recomputes the orphans server-side rather than sending ids. */
export const runSearchPrune = () => apiMutate(`${VITE_SEARCH_API}/index/prune`, "POST");

/** Recorded indexing runs, newest first. */
export const searchIndexRunsPath = (limit: number) =>
  `${VITE_SEARCH_API}/index/runs?limit=${limit}`;
